import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { LicenseType, licenseConfig } from './LicenseBadge';
import {
  IconLock,
  IconCheck,
  IconArrowRight,
  IconEye,
} from '../credit-plans/SVGIcons';
import { GlobalContext } from '../../providers/GlobalProvider';

interface RequiredLicenseProps {
  userLicense: LicenseType;
  minimumLicense: LicenseType;
  onUpgrade?: () => void;
}

const tierRank: Record<LicenseType, number> = {
  none: 0,
  standard: 1,
  pro: 2,
  business: 3,
  enterprise: 4,
};

const teamBadgeClassByLicense: Record<
  LicenseType,
  { badgeClass: string; dotClass: string }
> = {
  standard: {
    badgeClass: 'tm-badge tm-badge-standard',
    dotClass: 'tm-badge-dot tm-dot-standard',
  },
  pro: {
    badgeClass: 'tm-badge tm-badge-pro',
    dotClass: 'tm-badge-dot tm-dot-pro',
  },
  business: {
    badgeClass: 'tm-badge tm-badge-business',
    dotClass: 'tm-badge-dot tm-dot-business',
  },
  enterprise: {
    badgeClass: 'tm-badge tm-badge-enterprise',
    dotClass: 'tm-badge-dot tm-dot-enterprise',
  },
  none: {
    badgeClass: 'tm-badge tm-badge-none',
    dotClass: 'tm-badge-dot tm-dot-none',
  },
};

const RequiredLicense: React.FC<RequiredLicenseProps> = ({
  userLicense,
  minimumLicense,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const isSuperAdmin = cur_user?.bite_super_admin === true;
  const hasAccess =
    isSuperAdmin || tierRank[userLicense] >= tierRank[minimumLicense];
  if (hasAccess) {
    return null;
  }
  const userConfig = licenseConfig[userLicense];
  const minConfig = licenseConfig[minimumLicense];
  const userBadgeClasses = teamBadgeClassByLicense[userLicense];
  const minBadgeClasses = teamBadgeClassByLicense[minimumLicense];

  return (
    <div className={`rl-bar ${hasAccess ? 'rl-bar-ok' : 'rl-bar-warn'}`}>
      <div className='rl-left'>
        <span className='rl-icon-wrap'>
          {hasAccess ? <IconCheck /> : <IconLock />}
        </span>
        <span className='rl-your-label'>Your License:</span>
        <span className={userBadgeClasses.badgeClass}>
          <span className={userBadgeClasses.dotClass} />
          {userConfig.label}
        </span>
      </div>
      <div className='rl-divider-v' />
      <div className='rl-right'>
        <span className='rl-req-label'>Requires</span>
        <div className='rl-req-badges'>
          <span className={minBadgeClasses.badgeClass}>
            <span className={minBadgeClasses.dotClass} />
            {minConfig.label}
          </span>
          <span className='rl-req-label'>or higher</span>
          <span className='rl-view-only'>
            — You have view-only access <IconEye />
          </span>
        </div>
      </div>
      <Link
        className='rl-upgrade-link'
        to={{
          pathname: '/profile',
          state: {
            linkTab: 'Subscriptions',
          },
        }}
      >
        <button className='rl-upgrade-btn'>
          Upgrade <IconArrowRight />
        </button>
      </Link>
    </div>
  );
};

export default RequiredLicense;
